import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import { inventory as inventoryApi } from '@afosto/wms/api/Inventory';
import { locations as locationsApi } from '@afosto/wms/api/Locations';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import translations from './translations';

const ProductInventoryTable = ({ skus }) => {
  const [inventories, setInventories] = useState([]);
  const [isFetchingInventories, setIsFetchingInventories] = useState(true);

  useEffect(() => {
    const fetchInventory = async () => {
      setIsFetchingInventories(true);

      try {
        const response = await inventoryApi.searchInventorySummary({
          constraints: [
            {
              key: 'sku',
              operator: 'in',
              values: skus,
            },
            {
              key: 'is_managed',
              operator: 'eq',
              values: [true],
            },
            {
              key: 'state',
              operator: 'in',
              values: ['available', 'backordered'],
            },
          ],
        });
        const { data = [] } = response;

        const locationIds = data
          .map(inventory => inventory.location_id)
          .filter(locationId => locationId);

        const promises = [...new Set(locationIds)].map(locationId =>
          locationsApi.getLocation(locationId),
        );
        const results = await Promise.all(promises);
        const locations = results
          .reduce((acc, result) => [...acc, result?.data], [])
          .filter(location => location);

        const formattedInventories = data.reduce((acc, inventory) => {
          const location = findObjectInArray(locations, 'id', inventory.location_id);
          return {
            ...acc,
            [inventory.location_id]: {
              ...inventory,
              location,
              available: inventory.available + (acc?.[inventory?.location_id]?.available ?? 0),
              backordered:
                inventory.backordered + (acc?.[inventory?.location_id]?.backordered ?? 0),
              claimed: inventory.claimed + (acc?.[inventory?.location_id]?.claimed ?? 0),
              missing: inventory.missing + (acc?.[inventory?.location_id]?.missing ?? 0),
              total: inventory.total + (acc?.[inventory?.location_id]?.total ?? 0),
              transit: inventory.transit + (acc?.[inventory?.location_id]?.transit ?? 0),
            },
          };
        }, {});

        setInventories(Object.values(formattedInventories));
      } catch (err) {
        setInventories([]);
      } finally {
        setIsFetchingInventories(false);
      }
    };

    if (skus?.length) {
      fetchInventory();
    } else {
      setIsFetchingInventories(false);
    }
  }, [skus]);

  return (
    <LoadingWrapper isLoading={isFetchingInventories}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <FormattedMessage {...translations.headers.location} />
              </TableCell>
              <TableCell align="right">
                <FormattedMessage {...translations.headers.available} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inventories.map(inventory => (
              <TableRow key={`${inventory.sku}_${inventory.location_id}`}>
                <TableCell component="th" scope="row">
                  {inventory.location?.name}
                </TableCell>
                <TableCell align="right">{inventory.available}</TableCell>
              </TableRow>
            ))}
            {!inventories.length && (
              <TableRow>
                <TableCell colSpan={2}>
                  <FormattedMessage {...translations.emptyMessage} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </LoadingWrapper>
  );
};

ProductInventoryTable.propTypes = {
  skus: PropTypes.array,
};

ProductInventoryTable.defaultProps = {
  skus: [],
};

export default ProductInventoryTable;
